import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Module-level guard — survives React 18 StrictMode unmount/remount.
// One-time tokens can only be exchanged once; a duplicate returns 400.
const processedTokens = new Set<string>();

/**
 * Auth callback page — handles redirects from:
 * 1. Supabase OAuth (Google, GitHub) — Supabase handles the token exchange
 *    automatically via URL hash fragments.
 * 2. Flow360 Login (SimCloud portal) — we receive a one-time token in the
 *    `_token` query param that needs to be exchanged via our backend.
 * 3. Flow360 Login (local dev cookie flow) — local Flow360 UI sets cookies
 *    on .dev-simulation.cloud instead of passing _token.
 */
export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  function getPostAuthDestination(): string {
    const next = searchParams.get("next");
    if (next) return next;
    const stored = sessionStorage.getItem("cli_auth_return");
    if (stored) {
      sessionStorage.removeItem("cli_auth_return");
      return stored;
    }
    return "/";
  }

  useEffect(() => {
    const provider = searchParams.get("provider");
    const oneTimeToken = searchParams.get("_token");
    const destination = getPostAuthDestination();

    // Skip if we've already started processing this exact token
    if (oneTimeToken && processedTokens.has(oneTimeToken)) return;
    if (oneTimeToken) processedTokens.add(oneTimeToken);

    if (provider === "flow360" && oneTimeToken) {
      handleFlow360Token(oneTimeToken, destination);
    } else if (provider === "flow360") {
      handleFlow360Cookies(destination);
    } else {
      // Google / GitHub / Magic link — Supabase processes the hash fragment
      // automatically and fires onAuthStateChange when the session is ready.
      // We listen for that event instead of polling with setTimeout, because
      // hash-fragment exchange can take longer than 1s on slow connections.
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
          navigate(destination, { replace: true });
          return;
        }
        // No session yet — wait for Supabase to process the URL hash fragment.
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (_event, session) => {
            if (session) {
              subscription.unsubscribe();
              navigate(destination, { replace: true });
            }
          }
        );
        // Safety timeout — if nothing happens after 15 seconds, show error
        setTimeout(() => {
          subscription.unsubscribe();
          supabase.auth.getSession().then(({ data: { session: s } }) => {
            if (s) {
              navigate(destination, { replace: true });
            } else {
              setError("Authentication failed. Please try again.");
            }
          });
        }, 15000);
      });
    }
  }, [searchParams, navigate]);

  /**
   * Local dev flow: the local Flow360 UI sets cookies on .dev-simulation.cloud.
   * Read them, store in sessionStorage, and mint a Supabase session via backend.
   */
  async function handleFlow360Cookies(destination: string) {
    function getCookie(name: string): string | null {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
      return null;
    }

    const accessToken = getCookie("FLOW360_ACCESS_TOKEN");
    if (!accessToken) {
      setError("Flow360 login succeeded but no tokens found. Check that you're on thread.dev-simulation.cloud.");
      return;
    }

    sessionStorage.setItem("flow360_access_token", accessToken);
    const refreshToken = getCookie("FLOW360_REFRESH_TOKEN");
    if (refreshToken) sessionStorage.setItem("flow360_refresh_token", refreshToken);

    const cookieUser = getCookie("FLOW360_COOKIE_USER");
    if (cookieUser) {
      try {
        const user = JSON.parse(decodeURIComponent(cookieUser));
        if (user.email) sessionStorage.setItem("flow360_user_email", user.email);
      } catch { /* ignore parse errors */ }
    }

    try {
      const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
      const res = await fetch(`${API_BASE}/flow360/auth/status`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.ok) {
        const status = await res.json();
        if (status.user?.email) {
          sessionStorage.setItem("flow360_user_email", status.user.email);
        }
      }
    } catch {
      // Non-critical — we have the tokens either way
    }

    navigate(destination, { replace: true });
  }

  async function handleFlow360Token(oneTimeToken: string, destination: string) {
    try {
      // Exchange one-time token via our backend (avoids CORS)
      const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
      const res = await fetch(
        `${API_BASE}/flow360/auth/token/exchange?oneTimeToken=${encodeURIComponent(oneTimeToken)}`,
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Token exchange failed: ${text}`);
      }

      const data = await res.json();

      // Store Flow360 tokens for Flow360 API access
      if (data.auth?.accessToken) {
        sessionStorage.setItem("flow360_access_token", data.auth.accessToken);
      }
      if (data.auth?.refreshToken) {
        sessionStorage.setItem("flow360_refresh_token", data.auth.refreshToken);
      }
      if (data.cognito_sub) {
        sessionStorage.setItem("flow360_cognito_sub", data.cognito_sub);
      }
      if (data.user?.email) {
        sessionStorage.setItem("flow360_user_email", data.user.email);
      }

      // Establish Supabase session from the server-minted tokens.
      // The backend mints a real Supabase JWT via admin generate_link + verify,
      // so Flow360 users get the same auth level as Google/GitHub users.
      if (data.supabase_session?.access_token && data.supabase_session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.supabase_session.access_token,
          refresh_token: data.supabase_session.refresh_token,
        });

        if (sessionError) {
          console.error("Failed to set Supabase session:", sessionError);
          setError(`Supabase session error: ${sessionError.message}`);
          return;
        }

        navigate(destination, { replace: true });
        return;
      }

      // Fallback: no Supabase session minted (backend missing config)
      const email = data.user?.email;
      if (email) {
        console.warn("Flow360 login succeeded but no Supabase session was minted. Check backend SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY.");
        navigate(destination, { replace: true });
      } else {
        setError("No email returned from Flow360. Please try another login method.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Flow360 login failed");
    }
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          gap: "16px",
        }}
      >
        <p style={{ color: "var(--red-9)" }}>{error}</p>
        <button
          onClick={() => navigate("/", { replace: true })}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid var(--gray-7)",
            background: "var(--gray-3)",
            color: "var(--gray-12)",
            cursor: "pointer",
          }}
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <p style={{ color: "var(--gray-9)" }}>Completing sign in...</p>
    </div>
  );
}
